import { createClient } from '@supabase/supabase-js';

/**
 * Fonte única de verdade para URL Supabase / Functions.
 * Fail-closed: sem fallback para produção ou qualquer destino implícito.
 */
function assertValidSupabaseUrl(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.trim()) {
    throw new Error(
      '[supabase] VITE_PUBLIC_SUPABASE_URL ausente ou vazia. Configure o ambiente Supabase.'
    );
  }
  let parsed: URL;
  try {
    parsed = new URL(raw.trim());
  } catch {
    throw new Error('[supabase] VITE_PUBLIC_SUPABASE_URL não é uma URL válida.');
  }
  if (parsed.protocol !== 'https:') {
    throw new Error('[supabase] VITE_PUBLIC_SUPABASE_URL deve usar HTTPS.');
  }
  if (!parsed.hostname.endsWith('.supabase.co')) {
    throw new Error(
      '[supabase] VITE_PUBLIC_SUPABASE_URL deve apontar para um host *.supabase.co.'
    );
  }
  return parsed.origin;
}

function assertValidSupabaseAnonKey(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.trim()) {
    throw new Error(
      '[supabase] VITE_PUBLIC_SUPABASE_ANON_KEY ausente ou vazia. Configure o ambiente Supabase.'
    );
  }
  return raw.trim();
}

const rawSupabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const rawSupabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

/** URL validada sob demanda (fail-closed). Sem fallback. */
export function getSupabaseUrl(): string {
  return assertValidSupabaseUrl(rawSupabaseUrl);
}

/** Anon/publishable key validada sob demanda (fail-closed). Sem fallback. */
export function getSupabaseAnonKey(): string {
  return assertValidSupabaseAnonKey(rawSupabaseAnonKey);
}

/** Base das Edge Functions, derivada da URL configurada. */
export function getSupabaseFunctionsUrl(): string {
  return `${getSupabaseUrl()}/functions/v1`;
}

/** URL completa de uma Edge Function, derivada da configuração central. */
export function getSupabaseFunctionUrl(functionName: string): string {
  const name = String(functionName ?? '').replace(/^\/+/, '').trim();
  if (!name) {
    throw new Error('[supabase] Nome da Edge Function ausente.');
  }
  return `${getSupabaseFunctionsUrl()}/${name}`;
}

// Exportados para uso direto via fetch (bypassa o cliente JS).
// Valores brutos trimados — callers de negócio devem preferir getSupabaseUrl / getSupabaseFunctionUrl.
const supabaseUrl =
  typeof rawSupabaseUrl === 'string' && rawSupabaseUrl.trim() ? rawSupabaseUrl.trim() : '';
const supabaseAnonKey =
  typeof rawSupabaseAnonKey === 'string' && rawSupabaseAnonKey.trim()
    ? rawSupabaseAnonKey.trim()
    : '';

export { supabaseUrl, supabaseAnonKey };

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
      global: {
        // ✅ CRITICAL FIX: Prevent mobile browsers (iOS Safari, Android Chrome)
        // from caching Supabase REST GET responses. Without this, the 500ms poll
        // in EventoPage and the syncAll in EventSyncContext may return stale cached
        // data even after the DB is updated (e.g., override_mode=true), causing the
        // countdown to never update on mobile.
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      },
    })
  : null as any;

// Limpa tokens inválidos do localStorage
function limparTokensInvalidos() {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('sb-')) localStorage.removeItem(key);
    });
  } catch {
    // silencioso
  }
}

// Verifica se o erro é de refresh token inválido
export function isInvalidRefreshTokenError(error: any): boolean {
  if (!error) return false;
  const msg = (error?.message ?? '').toLowerCase();
  return (
    msg.includes('invalid refresh token') ||
    msg.includes('refresh token not found') ||
    msg.includes('token has expired') ||
    error?.status === 400 ||
    error?.code === 'refresh_token_not_found'
  );
}

// Listener global: detecta token inválido e faz logout automático
if (isSupabaseConfigured && supabase) {
  supabase.auth.onAuthStateChange((event: string, session: any) => {
    // Quando o refresh falha, o Supabase emite SIGNED_OUT sem sessão
    if (event === 'SIGNED_OUT' && !session) {
      limparTokensInvalidos();
    }
  });
}

export type Database = {
  public: {
    Tables: {
      usuarios: {
        Row: {
          id: string;
          nome: string;
          email: string;
          telefone: string;
          role: 'user' | 'vendor' | 'admin';
          status: 'active' | 'pending' | 'suspended';
          criado_em: string;
        };
        Insert: Omit<Database['public']['Tables']['usuarios']['Row'], 'id' | 'criado_em'>;
        Update: Partial<Database['public']['Tables']['usuarios']['Insert']>;
      };
      empresas: {
        Row: {
          id: string;
          nome_empresa: string;
          cnpj: string;
          email: string;
          email_comercial: string | null;
          telefone: string | null;
          telefone_comercial: string | null;
          descricao: string | null;
          aprovado: boolean;
          criado_em: string;
          usuario_id: string | null;
          status_verificacao: string | null;
          payout_mode: 'INTERNAL' | 'ASAAS_SPLIT';
          balance: number;
          pix_key: string | null;
          wallet_id: string | null;
          bloqueado_comissao: boolean;
          mercadopago_status: string | null;
          mercadopago_access_token: string | null;
        };
        Insert: Omit<Database['public']['Tables']['empresas']['Row'], 'id' | 'criado_em'>;
        Update: Partial<Database['public']['Tables']['empresas']['Insert']>;
      };
      eventos: {
        Row: {
          id: string;
          empresa_id: string;
          nome_evento: string;
          data_inicio: string;
          data_fim: string;
          status: 'pending' | 'active' | 'completed' | 'cancelled';
          criado_em: string;
        };
        Insert: Omit<Database['public']['Tables']['eventos']['Row'], 'id' | 'criado_em'>;
        Update: Partial<Database['public']['Tables']['eventos']['Insert']>;
      };
      itens: {
        Row: {
          id: string;
          evento_id: string;
          nome_item: string;
          descricao: string;
          imagem: string;
          valor_inicial: number;
          valor_atual: number;
          incremento_lance: number;
          tempo_disputa_segundos: number;
          status: 'pending' | 'pre_dispute' | 'active' | 'completed' | 'cancelled';
          vencedor_id: string | null;
          criado_em: string;
        };
        Insert: Omit<Database['public']['Tables']['itens']['Row'], 'id' | 'criado_em'>;
        Update: Partial<Database['public']['Tables']['itens']['Insert']>;
      };
      lances: {
        Row: {
          id: string;
          item_id: string;
          usuario_id: string;
          valor_lance: number;
          criado_em: string;
        };
        Insert: Omit<Database['public']['Tables']['lances']['Row'], 'id' | 'criado_em'>;
        Update: Partial<Database['public']['Tables']['lances']['Insert']>;
      };
      pagamentos: {
        Row: {
          id: string;
          usuario_id: string;
          item_id: string;
          valor: number;
          status_pagamento: 'pending' | 'processing' | 'completed' | 'failed';
          metodo: string;
          criado_em: string;
        };
        Insert: Omit<Database['public']['Tables']['pagamentos']['Row'], 'id' | 'criado_em'>;
        Update: Partial<Database['public']['Tables']['pagamentos']['Insert']>;
      };
      comissoes: {
        Row: {
          id: string;
          empresa_id: string;
          pagamento_id: string;
          item_id: string | null;
          valor_venda: number;
          valor_comissao: number;
          status: 'pendente' | 'vencida' | 'cobrada' | 'inadimplente' | 'paga';
          vencimento: string;
          mp_payment_id: string | null;
          mp_qr_code: string | null;
          mp_qr_code_base64: string | null;
          mp_pix_copy_paste: string | null;
          criado_em: string;
          pago_em: string | null;
        };
        Insert: Omit<Database['public']['Tables']['comissoes']['Row'], 'id' | 'criado_em'>;
        Update: Partial<Database['public']['Tables']['comissoes']['Insert']>;
      };
    };
  };
};
